import React, {useState} from "react"
import {Grid} from "@mui/material";
import CCOHeadline from "../../fundamentals/CCOHeadline";
import CCORichText from "../../fundamentals/CCORichText/CCORichText";
import {Maybe} from "graphql/jsutils/Maybe";
import CCOAccordion from "../../fundamentals/CCOAccordion";
import CCOLink from "../../fundamentals/CCOLink";
import C039Author from "../../content/C039Author/C039Author";
import {RichTextElement} from "../../fundamentals/CCORichText/CCORichTextHelper";
import {SosAuthor, SosPublication} from "../../../../packages/graphql-layer/src";


export interface Props {
    sosPublication: SosPublication
}


function P011PublicationAccordion(props: Props) {
    const accordions = getAccordions(props.sosPublication);
    const [expandedAccordions, setExpandedAccordions] = useState([accordions.length > 0 ? accordions[0].id : '']);
    const handleAccordionChange = (accordionId: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
        isExpanded ? setExpandedAccordions([...expandedAccordions, accordionId]) : setExpandedAccordions(expandedAccordions.filter(id => id !== accordionId));
    };
    return <React.Fragment key='accordions'>
        {accordions.length > 0 &&
            accordions.map((accordion, index) => {
                return <CCOAccordion key={'accordion_' + index} id={accordion.id} title={accordion.title}
                                     initiallyExpanded={index === 0}
                                     expanded={expandedAccordions.includes(accordion.id)}
                                     onChange={handleAccordionChange(accordion.id)}>
                    {accordion.content()}
                </CCOAccordion>
            })}
    </React.Fragment>
}

function getAccordions(sosPublication: SosPublication) {
    const details = getAccordionDetails(sosPublication);
    const directory = getAccordionDirectory(sosPublication);
    const authors = getAccordionAuthors(sosPublication);
    const moreOfSeries = getAccordionMoreOfSeries(sosPublication);

    const accordions = [];
    details && accordions.push(details);
    directory && accordions.push(directory);
    authors && accordions.push(authors);
    moreOfSeries && accordions.push(moreOfSeries);
    return accordions;
}

function getAccordionDetails(sosPublication: SosPublication) {
    return sosPublication.details && {
        id: sosPublication.id,
        title: 'Beschreibung',
        content: () => getAccordionDetailsContent(sosPublication)
    }
}

function getAccordionDetailsContent(sosPublication: SosPublication) {
    return <CCORichText metadataId={sosPublication.id} key={'details'}
                        propertyName='properties.details'
                        textAsTree={sosPublication.details!.textAsTree as RichTextElement}/>
}

function getAccordionDirectory(sosPublication: SosPublication) {
    return sosPublication.directory && {
        id: sosPublication.id + '_inhalt',
        title: 'Inhaltsverzeichnis',
        content: () => getAccordionDirectoryContent(sosPublication)
    }
}

function getAccordionDirectoryContent(sosPublication: SosPublication) {
    return <CCORichText metadataId={sosPublication.id} key={'directory'}
                        propertyName='properties.directory'
                        textAsTree={sosPublication.directory!.textAsTree as RichTextElement}/>
}

function getAccordionAuthors(sosPublication: SosPublication) {
    return sosPublication.vitae?.length && sosPublication.vitae?.length > 0 &&
        {
            id: sosPublication.id + '_autor',
            title: 'Autor*innen',
            content: () => getAccordionAuthorsContent(sosPublication)
        }
}

function getAccordionAuthorsContent(sosPublication: SosPublication) {
    return <React.Fragment key='authors'>
        {sosPublication.vitae?.map((author: Maybe<SosAuthor>, index) => {
            return <React.Fragment key={'author_' + index}>
                <CCOHeadline metadataId={sosPublication.id} key={index} type='h6' justify='left'
                             headline={(author?.authortitle ? author?.authortitle + ' ' : '') + author?.name + ' ' + author?.surname}/>
                <CCORichText key={index} textAsTree={author?.teaserText!.textAsTree as RichTextElement}/>
                <Grid sx={{my: 3}}></Grid>
            </React.Fragment>
        })}
    </React.Fragment>
}

function getAccordionMoreOfSeries(sosPublication: SosPublication) {
    return sosPublication.moreofseries?.length && sosPublication.moreofseries?.length > 0 &&
        {
            id: sosPublication.id + '_mehr',
            title: 'Mehr aus dieser Reihe',
            content: () => getAccordionMoreOfSeriesContent(sosPublication)
        }
}

function getAccordionMoreOfSeriesContent(sosPublication: SosPublication) {
    return <React.Fragment key='more'>
        {sosPublication.moreofseries?.map((item: Maybe<SosPublication>, index) => {
            return <Grid key={'more_' + index} sx={{'a': {color: 'primary.main'}}}>
                <CCOLink target={item!} contentId={item?.id} name={item?.title as any}/>
                <C039Author publication={item!}/>
                <br/>
                {item?.extDisplayedDate && item?.extDisplayedDate?.substr(0, 4)}
                <br/>
                <Grid sx={{my: 3}}></Grid>
            </Grid>
        })}
    </React.Fragment>
}

export default P011PublicationAccordion